
import { Search } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { BuyerSearch } from "@/components/opportunity-finder/BuyerSearch";
import { ExpiringContractsTable } from "@/components/opportunity-finder/ExpiringContractsTable";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";

interface Contract {
  "publication-number"?: string;
  "buyer-name"?: string;
  "buyer-country"?: string;
  "winner-name"?: string;
  "winner-country"?: string;
  "organisation-name-tenderer"?: string;
  "contract-title"?: string;
  "notice-title"?: string;
  "total-value"?: string;
  "total-value-cur"?: string;
  "publication-date"?: string;
  "contract-conclusion-date"?: string;
  "contract-duration-period-lot"?: string;
  "contract-duration-period-part"?: string;
  "duration-period-value-lot"?: string;
  "duration-period-value-part"?: string;
  "duration-period-unit-lot"?: string;
  "duration-period-unit-part"?: string;
  "classification-cpv"?: string[];
  "estimated-end-date"?: string;
  [key: string]: any;
}

const OpportunityFinder = () => {
  const { t } = useTranslation("opportunityFinder");
  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedBuyer, setSelectedBuyer] = useState<string | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalContractsFound, setTotalContractsFound] = useState<number>(0);

  const handleBuyerSelect = (buyer: string) => {
    setSelectedBuyer(buyer);
    searchContracts(buyer);
  };

  const searchContracts = async (buyerName: string) => {
    setIsLoading(true);
    try {
      toast({
        title: t("searchingContracts", { buyer: buyerName }),
        description: t("filters.timeframeOptions.allContracts"),
      });

      const { data, error } = await supabase.functions.invoke(
        "search-expiring-contracts",
        {
          body: {
            buyerName,
            timeframe: "0months", // Always use "0months" (all contracts)
            limit: 100,
          },
        }
      );

      if (error) {
        toast({
          title: t("searchFailed"),
          description: t("searchFailedDescription"),
          variant: "destructive",
        });
        throw error;
      }

      console.log("Edge function response:", data);
      
      if (data?.data && Array.isArray(data.data)) {
        console.log(`Received ${data.data.length} contracts from edge function`);
        if (data.data.length > 0) {
          console.log("First contract sample:", data.data[0]);
        }
        
        setContracts(data.data);
        setTotalContractsFound(data.totalCount || data.data.length);

        toast({
          title: t("buyerSelected"),
          description: `${data.data.length} ${t("search.contracts").toLowerCase()}`,
        });
      } else {
        console.error("Unexpected data structure from edge function:", data);
        setContracts([]);
        setTotalContractsFound(0);
        toast({
          title: t("searchFailed"),
          description: t("searchFailedDescription"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching expiring contracts:", error);
      setContracts([]);
      setTotalContractsFound(0);
      toast({
        title: t("searchFailed"),
        description: t("searchFailedDescription"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout 
      title={t("pageTitle")} 
      description={t("pageDescription")} 
      icon={<Search className="h-6 w-6" />}
    >
      <div className="container max-w-7xl mx-auto py-6">
        <div className="mb-8">
          <BuyerSearch onSelectBuyer={handleBuyerSelect} />
        </div>
        
        {selectedBuyer && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-semibold">
                  {t("contractsFor", { buyer: selectedBuyer })}
                  {totalContractsFound > 0 && (
                    <span className="text-sm font-normal text-muted-foreground ml-2">
                      ({contracts.length} {t("shownOfTotal", { total: totalContractsFound })})
                    </span>
                  )}
                </h2>
              </div>
            </div>

            <ExpiringContractsTable 
              contracts={contracts} 
              isLoading={isLoading} 
            />
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default OpportunityFinder;
